import { ChangeEventHandler, FC, useCallback, useMemo, useRef } from 'react';

import { AsyncSearch, ExpirationDate, Loader, SearchableSelect, SearchableSelectOption } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { getUnitByUnitNumber } from 'services';
import { Customer, EquipmentType } from 'types';

import { useEditUnit } from './edit-unit.context';
import './edit-unit.scss';

const EditUnitForm: FC = () => {
  const { unit, unitId, unitNumberRef, handleReset, handleSubmit, setUnit } = useEditUnit();
  const { searchCustomers } = useCustomerAPI();
  const { unitStatusOptions, unitGradeOptions } = useLookups();
  const { containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries } = useSizeTypes();

  const sizeTypeEntries: [string, string[]][] = useMemo(() => {
    switch (unit?.equipmentType) {
      case EquipmentType.CHASSIS:
        return chassisSizeTypeEntries;
      case EquipmentType.CONTAINER:
        return containerSizeTypeEntries;
      case EquipmentType.GENSET:
        return gensetSizeTypeEntries;
      default:
        return [];
    }
  }, [unit?.equipmentType, chassisSizeTypeEntries, containerSizeTypeEntries, gensetSizeTypeEntries]);

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return sizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [sizeTypeEntries]);

  const timeout = useRef<NodeJS.Timeout>();
  const validateUnitNumber = useMemo(() => {
    clearTimeout(timeout.current);
    return async (unitNumber: string) => {
      const response = await getUnitByUnitNumber(unitNumber);
      if (!response || response.id === unitId) unitNumberRef.current?.setCustomValidity('');
      else {
        unitNumberRef.current?.setCustomValidity('Unit in yard');
        unitNumberRef.current?.reportValidity();
      }
    };
  }, [unitId, unitNumberRef]);

  const setUnitNumber: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      const unitNumber = e.target.value.toUpperCase();
      setUnit(p => p && { ...p, unitNumber });
      if (unitNumber) validateUnitNumber(unitNumber);
    },
    [setUnit, validateUnitNumber]
  );

  const fmcsaDateValue = useMemo(() => unit?.fmcsaDate, [unit?.fmcsaDate]);
  const lpExpDateValue = useMemo(() => unit?.licensePlateExpirationDate, [unit?.licensePlateExpirationDate]);
  const setLpExpDate = useCallback(
    (licensePlateExpirationDate?: number) => setUnit(p => p && { ...p, licensePlateExpirationDate }),
    [setUnit]
  );
  const setFmcsaDate = useCallback((fmcsaDate?: number) => setUnit(p => p && { ...p, fmcsaDate }), [setUnit]);
  const setCustomer = useCallback((customer?: Customer) => setUnit(p => p && { ...p, customer }), [setUnit]);

  return (
    <form className="page" id="edit-unit" onReset={handleReset} onSubmit={handleSubmit}>
      <div className="page-header">
        <h1>UNIT #{unit?.unitNumber ?? 'N/A'}</h1>
        <div className="d-flex g-3">
          <button className="btn-outline" type="reset">
            CANCEL
          </button>
          <button className="btn-primary" type="submit">
            SUBMIT
          </button>
        </div>
      </div>
      <div className="page-content">
        <Loader loading={unit === undefined}>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>UNIT DETAILS</h4>
            </div>
            <div className="card-body">
              <div className={'grid-4 p-2'}>
                <div>
                  <h6 className="text-info-dark">UNIT NUMBER</h6>
                  <input
                    className="mt-2"
                    type="text"
                    ref={unitNumberRef}
                    value={unit?.unitNumber ?? ''}
                    onChange={setUnitNumber}
                    data-cy="edit-unit-number-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">SIZE TYPE</h6>
                  <div className="mt-2 select">
                    <SearchableSelect
                      onValueSelected={sizeType => setUnit(p => p && { ...p, sizeType })}
                      options={sizeTypeOptions}
                      value={unit?.sizeType ?? ''}
                      required
                      data-cy="edit-unit-size-type-input"
                    />
                  </div>
                </div>
                <div>
                  <h6 className="text-info-dark">STATUS</h6>
                  <div className="mt-2 select">
                    <SearchableSelect
                      disabled
                      onValueSelected={(unitStatus: any) => setUnit(p => p && { ...p, unitStatus })}
                      options={unitStatusOptions.map(us => ({ value: us, label: us }))}
                      value={unit?.unitStatus ?? ''}
                      data-cy="edit-unit-status-input"
                    />
                  </div>
                </div>
                <div>
                  <h6 className="text-info-dark">STATUS NOTES</h6>
                  <input
                    disabled
                    className="mt-2"
                    type="text"
                    defaultValue={unit?.statusReason ?? ''}
                    onChange={e => setUnit(p => p && { ...p, statusReason: e.target.value })}
                    data-cy="edit-unit-status-notes-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">ACCOUNT</h6>
                  <div className="mt-2 w-100">
                    <AsyncSearch<Customer>
                      formatOption={({ name }) => name}
                      formatValue={customer => customer?.name ?? ''}
                      handleSelect={setCustomer}
                      search={searchCustomers}
                      value={unit?.customer}
                      data-cy="edit-unit-customer-search"
                    />
                  </div>
                </div>
                <div>
                  <h6 className="text-info-dark">LOCATION</h6>
                  <input className="mt-2" type="text" defaultValue={unit?.location?.name} disabled />
                </div>
                <div>
                  <h6 className="text-info-dark">GRADE</h6>
                  <div className="mt-2 select">
                    <SearchableSelect
                      onValueSelected={(unitGrade: any) => setUnit(p => p && { ...p, unitGrade })}
                      options={unitGradeOptions.map(us => ({ value: us, label: us }))}
                      value={unit?.unitGrade ?? ''}
                      data-cy="edit-unit-grade-input"
                    />
                  </div>
                </div>
                <div>
                  <h6 className="text-info-dark">NOTES</h6>
                  <input
                    className="mt-2"
                    type="text"
                    defaultValue={unit?.notes ?? ''}
                    onChange={e => setUnit(p => p && { ...p, notes: e.target.value })}
                    data-cy="edit-unit-notes-input"
                  />
                </div>
                {unit?.equipmentType === EquipmentType.CONTAINER && (
                  <div>
                    <h6 className="text-info-dark">LOADED</h6>
                    <input
                      className="mt-3"
                      type="checkbox"
                      checked={unit?.loaded ?? false}
                      onChange={e => setUnit(p => p && { ...p, loaded: e.target.checked })}
                      data-cy="edit-unit-loaded-checkbox"
                    />
                  </div>
                )}
                <div className="col-start-3">
                  <h6 className="text-info-dark">HOLD</h6>
                  <input
                    className="mt-3"
                    type="checkbox"
                    checked={unit?.onHold ?? false}
                    onChange={e => setUnit(p => p && { ...p, onHold: e.target.checked })}
                    data-cy="edit-unit-on-hold-checkbox"
                  />
                </div>
                <div className="col-start-4">
                  <h6 className="text-info-dark">HOLD NOTES</h6>
                  <input
                    className="mt-2"
                    type="text"
                    defaultValue={unit?.holdNotes ?? ''}
                    onChange={e => setUnit(p => p && { ...p, holdNotes: e.target.value })}
                    data-cy="edit-unit-hold-notes-input"
                  />
                </div>
              </div>
            </div>
          </div>
          {unit?.equipmentType === EquipmentType.CONTAINER && (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>CONTAINER DETAILS</h4>
              </div>
              <div className="card-body">
                <div className="grid-6 p-2">
                  <div>
                    <h6>SEAL #1</h6>
                    <input
                      className="mt-2"
                      type="text"
                      value={unit.seal0 ?? ''}
                      onChange={e => setUnit(p => p && { ...p, seal0: e.target.value })}
                      disabled={!unit?.loaded}
                    />
                  </div>
                  <div>
                    <h6>SEAL #2</h6>
                    <input
                      className="mt-2"
                      type="text"
                      value={unit.seal1 ?? ''}
                      onChange={e => setUnit(p => p && { ...p, seal1: e.target.value })}
                      disabled={!unit?.loaded}
                    />
                  </div>
                  <div>
                    <h6>SEAL #3</h6>
                    <input
                      className="mt-2"
                      type="text"
                      value={unit.seal2 ?? ''}
                      onChange={e => setUnit(p => p && { ...p, seal2: e.target.value })}
                      disabled={!unit?.loaded}
                    />
                  </div>
                  <div>
                    <h6 className="text-info-dark">HAZARD</h6>
                    <input
                      className="mt-3"
                      type="checkbox"
                      checked={unit?.hazard ?? false}
                      onChange={e => setUnit(p => p && { ...p, hazard: e.target.checked })}
                      disabled={!unit?.loaded}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {(unit?.equipmentType === EquipmentType.CHASSIS || unit?.sizeType?.endsWith('TR')) && (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>CHASSIS DETAILS</h4>
              </div>
              <div className="card-body">
                <div className="grid-6 p-2">
                  <div>
                    <h6 className="text-info-dark">LICENSE PLATE</h6>
                    <input
                      className="mt-2"
                      type="text"
                      value={unit?.licensePlate ?? ''}
                      onChange={e => setUnit(p => p && { ...p, licensePlate: e.target.value })}
                    />
                  </div>
                  <div>
                    <h6 className="text-info-dark">LP EXPIRATION</h6>
                    <div className="mt-2">
                      <ExpirationDate key="lpexp" date={lpExpDateValue} setDate={setLpExpDate} />
                    </div>
                  </div>
                  <div>
                    <h6 className="text-info-dark">FMCSA DATE</h6>
                    <div className="mt-2">
                      <ExpirationDate key="fmcsa" date={fmcsaDateValue} setDate={setFmcsaDate} />
                    </div>
                  </div>
                  <div className="grid-2">
                    <div>
                      <h6 className="text-info-dark">FLATBED</h6>
                      <input
                        disabled
                        className="mt-3"
                        type="checkbox"
                        checked={unit?.flatbed ?? false}
                        onChange={e => setUnit(p => p && { ...p, flatbed: e.target.checked })}
                      />
                    </div>
                    <div>
                      <h6 className="text-info-dark">OWNER</h6>
                      <input
                        disabled
                        className="mt-3"
                        type="checkbox"
                        checked={unit?.ownerChassis ?? false}
                        onChange={e => setUnit(p => p && { ...p, ownerChassis: e.target.checked })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {unit?.equipmentType === EquipmentType.GENSET && (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>GENSET DETAILS</h4>
              </div>
              <div className="card-body">
                <div className="grid-6 p-2">
                  <div className="position-relative">
                    <h6 className="text-info-dark">FUEL LEVEL</h6>
                    <input
                      className="mt-2"
                      type="number"
                      value={unit?.fuelLevel ?? ''}
                      onChange={e => {
                        const value = Number(e.target.value);
                        if (!isNaN(value)) setUnit(p => p && { ...p, fuelLevel: Math.max(0, Math.min(4, value)) });
                      }}
                    />
                    <p className="end-adornment">/ 4</p>
                  </div>
                  <div>
                    <h6 className="text-info-dark">CARB COMPLIANT</h6>
                    <input
                      className="mt-3"
                      type="checkbox"
                      checked={unit?.carbCompliant ?? false}
                      onChange={e => setUnit(p => p && { ...p, carbCompliant: e.target.checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {unit?.equipmentType === EquipmentType.CONTAINER && /\d\d(?:HR|R[CFST]|VT)/.test(unit?.sizeType ?? '') && (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>REEFER DETAILS</h4>
              </div>
              <div className="card-body">
                <div className="grid-6 p-2">
                  <div className="position-relative">
                    <h6 className="text-info-dark">TEMPERATURE</h6>
                    <input
                      className="mt-2"
                      type="number"
                      step="any"
                      value={unit?.temperature ?? ''}
                      onChange={e => {
                        const value = Math.max(-100, Math.min(100, Number(e.target.value)));
                        if (!isNaN(value)) setUnit(p => p && { ...p, temperature: value });
                      }}
                    />
                    <p className="end-adornment">° C</p>
                  </div>
                  <div className="position-relative">
                    <h6 className="text-info-dark">VENTILATION</h6>
                    <input
                      className="mt-3"
                      type="number"
                      value={unit?.ventilation ?? ''}
                      onChange={e => setUnit(p => p && { ...p, ventilation: e.target.value })}
                    />
                    <p className="end-adornment">%</p>
                  </div>
                  <div className="position-relative">
                    <h6 className="text-info-dark">HUMIDITY</h6>
                    <input
                      className="mt-2"
                      type="number"
                      step="any"
                      value={unit?.humidity ?? ''}
                      onChange={e => {
                        const value = Number(e.target.value);
                        if (!isNaN(value)) setUnit(p => p && { ...p, humidity: Math.max(0, Math.min(100, value)) });
                      }}
                    />
                    <p className="end-adornment">%</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Loader>
      </div>
    </form>
  );
};

export default EditUnitForm;
